// frontend/src/components/shop/ShopPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { shopAPI } from '../../api/shop';
import ProductCard from './ProductCard';
import ProductDescriptionModal from './ProductDescriptionModal';
import Checkout from './Checkout';
import { ShoppingCart, Filter, X, Menu, ChevronRight, Plus, Trash2, Pencil } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const DRAFT_CART_STORAGE_KEY = 'leikinteam_shop_draft_cart_v1';

const SUBCATEGORY_RULES = [
  { id: 'gels', label: 'Гели', keywords: ['гель', 'gel', 'hydrogel'] },
  { id: 'bars', label: 'Батончики', keywords: ['батон', 'bar', 'пирож'] },
  { id: 'drinks', label: 'Изотоники и напитки', keywords: ['изотон', 'hypoton', 'carbo', 'напит'] },
  { id: 'electrolytes', label: 'Электролиты', keywords: ['электролит', 'salt', 'солев', 'капсул'] },
  { id: 'gummies', label: 'Мармелад и жевательные', keywords: ['мармел', 'жеват', 'chew'] },
  { id: 'recovery', label: 'Восстановление и протеин', keywords: ['recovery', 'протеин', 'muscle'] },
  { id: 'creatine', label: 'Креатин', keywords: ['креатин', 'creatine'] },
  { id: 'accessories', label: 'Аксессуары', keywords: ['бутыл', 'soft flask', 'фляг'] },
  { id: 'delivery', label: 'Доставка', keywords: ['доставка'] },
  { id: 'other', label: 'Другое', keywords: [] },
];

const normalize = (value = '') =>
  value
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/\s+/g, ' ')
    .trim();

const getSubcategoryByProduct = (productName = '') => {
  const normalizedName = normalize(productName);
  const matchedRule = SUBCATEGORY_RULES.find(
    (rule) => rule.id !== 'other' && rule.keywords.some((keyword) => normalizedName.includes(keyword)),
  );

  return matchedRule || SUBCATEGORY_RULES.find((rule) => rule.id === 'other');
};

const collectSubcategories = (items = []) => {
  const counters = {};

  items.forEach((product) => {
    const rule = getSubcategoryByProduct(product.name);
    if (!rule) return;
    counters[rule.id] = (counters[rule.id] || 0) + 1;
  });

  return SUBCATEGORY_RULES.filter((rule) => counters[rule.id]).map((rule) => ({
    id: rule.id,
    label: rule.label,
    count: counters[rule.id],
  }));
};

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  // Локальная корзина (черновик): productId -> { product, quantity }
  const [draftCart, setDraftCart] = useState({});
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantityInputs, setQuantityInputs] = useState({});
  
  // Фильтры
  const [sortBy, setSortBy] = useState('default');
  const [showFilters, setShowFilters] = useState(false);
  
  // Мобильное меню категорий
  const [showMobileCategories, setShowMobileCategories] = useState(false);
  const [selectedBrandGroup, setSelectedBrandGroup] = useState(null);

  const { isAdmin } = useAuth();
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminTab, setAdminTab] = useState('product');
  const [adminLoading, setAdminLoading] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    description: '',
    actionType: null,
    payload: null,
  });
  const [categoryForm, setCategoryForm] = useState({ name: '', slug: '', order: 0 });
  const [productForm, setProductForm] = useState({
    category_id: '',
    name: '',
    description: '',
    price: '',
    pack_quantity: '',
    external_image_url: '',
    image: null,
    is_available: true,
    order: 0,
  });

  const emptyCategoryForm = { name: '', slug: '', order: 0 };
  const emptyProductForm = {
    category_id: '',
    name: '',
    description: '',
    price: '',
    pack_quantity: '',
    external_image_url: '',
    image: null,
    is_available: true,
    order: 0,
  };

  useEffect(() => {
    try {
      const savedDraftCart = window.localStorage.getItem(DRAFT_CART_STORAGE_KEY);
      if (!savedDraftCart) return;

      const parsedDraftCart = JSON.parse(savedDraftCart);
      if (parsedDraftCart && typeof parsedDraftCart === 'object') {
        setDraftCart(parsedDraftCart);
      }
    } catch (error) {
      console.error('Error restoring draft cart:', error);
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(DRAFT_CART_STORAGE_KEY, JSON.stringify(draftCart));
    } catch (error) {
      console.error('Error saving draft cart:', error);
    }
  }, [draftCart]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Группы брендов (SiS / Sponser) поверх обычных категорий
  const brandGroups = useMemo(() => {
    const groups = {
      sis: {
        id: 'sis',
        title: 'SiS',
        description: 'Энергетические гели, напитки и батончики для выносливых тренировок',
        categories: [],
        color: 'from-blue-500 to-blue-600',
      },
      sponser: {
        id: 'sponser',
        title: 'Sponser',
        description: 'Питание для выносливости, восстановления и ежедневных тренировок',
        categories: [],
        color: 'from-purple-500 to-purple-600',
      },
    };

    categories.forEach((cat) => {
      const name = (cat.name || '').toLowerCase();
      if (name.includes('sis')) {
        groups.sis.categories.push(cat);
      } else if (name.includes('sponser') || name.includes('sponsor')) {
        groups.sponser.categories.push(cat);
      }
    });

    return groups;
  }, [categories]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      
      let products = [];
      let categories = [];
      
      try {
        let productsData = await shopAPI.getProducts();
        products = Array.isArray(productsData) ? productsData : (productsData?.results || productsData || []);
      } catch (productsError) {
        console.error('Error loading products:', productsError);
        products = [];
      }
      
      try {
        const categoriesData = await shopAPI.getCategories();
        categories = Array.isArray(categoriesData) ? categoriesData : (categoriesData?.results || categoriesData || []);
      } catch (categoriesError) {
        console.error('Error loading categories:', categoriesError);
        categories = [];
      }
      
      setProducts(products);
      setCategories(categories);
      setDraftCart((prev) => {
        if (!prev || typeof prev !== 'object') {
          return {};
        }

        const nextDraftCart = { ...prev };
        products.forEach((product) => {
          if (nextDraftCart[product.id]) {
            nextDraftCart[product.id] = {
              ...nextDraftCart[product.id],
              product,
            };
          }
        });

        return nextDraftCart;
      });
      
      // Инициализируем количество для каждого товара
      const initialQuantities = {};
      products.forEach(product => {
        initialQuantities[product.id] = 1;
      });
      setQuantityInputs(initialQuantities);
    } catch (error) {
      console.error('Unexpected error loading shop data:', error);
      toast.error('Ошибка загрузки данных магазина');
      setProducts([]);
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const buildProductPayload = () => {
    const basePayload = {
      category_id: productForm.category_id ? Number(productForm.category_id) : null,
      name: productForm.name,
      description: productForm.description || '',
      price: productForm.price,
      pack_quantity: productForm.pack_quantity ? Number(productForm.pack_quantity) : null,
      external_image_url: productForm.external_image_url || '',
      is_available: !!productForm.is_available,
      order: Number(productForm.order || 0),
    };
    const hasImageFile = productForm.image instanceof File;
    if (!hasImageFile) {
      return basePayload;
    }

    const payload = new FormData();
    Object.entries(basePayload).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      payload.append(key, String(value));
    });
    payload.append('image', productForm.image);
    return payload;
  };

  const handleCreateCategory = async () => {
    if (!categoryForm.name || !categoryForm.slug) {
      toast.error('Введите название и slug категории');
      return;
    }

    try {
      setAdminLoading(true);
      await shopAPI.createCategory({
        name: categoryForm.name,
        slug: categoryForm.slug,
        order: Number(categoryForm.order || 0),
      });
      toast.success('Категория добавлена');
      setCategoryForm(emptyCategoryForm);
      setEditingCategoryId(null);
      setShowCategoryModal(false);
      await loadData();
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Не удалось добавить категорию');
    } finally {
      setAdminLoading(false);
    }
  };

  const handleCreateProduct = async () => {
    if (!productForm.name || !productForm.price) {
      toast.error('Введите название и цену товара');
      return;
    }

    try {
      setAdminLoading(true);
      await shopAPI.createProduct(buildProductPayload());
      toast.success('Товар добавлен');
      setProductForm(emptyProductForm);
      setEditingProductId(null);
      setShowProductModal(false);
      await loadData();
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error('Не удалось добавить товар');
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProductId) return;
    if (!productForm.name || !productForm.price) {
      toast.error('Введите название и цену товара');
      return;
    }

    try {
      setAdminLoading(true);
      await shopAPI.updateProduct(editingProductId, buildProductPayload());
      toast.success('Товар обновлен');
      setProductForm(emptyProductForm);
      setEditingProductId(null);
      setShowProductModal(false);
      await loadData();
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Не удалось обновить товар');
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategoryId) return;
    if (!categoryForm.name || !categoryForm.slug) {
      toast.error('Введите название и slug категории');
      return;
    }

    try {
      setAdminLoading(true);
      await shopAPI.updateCategory(editingCategoryId, {
        name: categoryForm.name,
        slug: categoryForm.slug,
        order: Number(categoryForm.order || 0),
      });
      toast.success('Категория обновлена');
      setCategoryForm(emptyCategoryForm);
      setEditingCategoryId(null);
      setShowCategoryModal(false);
      await loadData();
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Не удалось обновить категорию');
    } finally {
      setAdminLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      setAdminLoading(true);
      await shopAPI.deleteProduct(productId);
      toast.success('Товар удален');
      await loadData();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Не удалось удалить товар');
    } finally {
      setAdminLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      setAdminLoading(true);
      await shopAPI.deleteCategory(categoryId);
      toast.success('Категория удалена');
      await loadData();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Не удалось удалить категорию');
    } finally {
      setAdminLoading(false);
    }
  };

  const openConfirm = ({ title, description, actionType, payload }) => {
    setConfirmModal({
      isOpen: true,
      title,
      description,
      actionType,
      payload,
    });
  };

  const closeConfirm = () => {
    setConfirmModal({
      isOpen: false,
      title: '',
      description: '',
      actionType: null,
      payload: null,
    });
  };

  const confirmAction = async () => {
    const { actionType, payload } = confirmModal;
    closeConfirm();

    if (actionType === 'delete_product' && payload?.productId) {
      await handleDeleteProduct(payload.productId);
      return;
    }
    if (actionType === 'delete_category' && payload?.categoryId) {
      await handleDeleteCategory(payload.categoryId);
      return;
    }
    if (actionType === 'create_product') {
      await handleCreateProduct();
      return;
    }
    if (actionType === 'update_product') {
      await handleUpdateProduct();
      return;
    }
    if (actionType === 'create_category') {
      await handleCreateCategory();
      return;
    }
    if (actionType === 'update_category') {
      await handleUpdateCategory();
      return;
    }
  };

  const openCreateProductModal = () => {
    setEditingProductId(null);
    setProductForm(emptyProductForm);
    setShowProductModal(true);
  };

  const openEditProductModal = (product) => {
    setEditingProductId(product.id);
    setProductForm({
      category_id: product?.category?.id || '',
      name: product?.name || '',
      description: product?.description || '',
      price: product?.price ?? '',
      pack_quantity: product?.pack_quantity ?? '',
      external_image_url: product?.raw_image_url || '',
      image: null,
      is_available: Boolean(product?.is_available),
      order: product?.order ?? 0,
    });
    setShowProductModal(true);
  };

  const closeProductModal = () => {
    setShowProductModal(false);
    setEditingProductId(null);
    setProductForm(emptyProductForm);
  };

  const openCreateCategoryModal = () => {
    setEditingCategoryId(null);
    setCategoryForm(emptyCategoryForm);
    setShowCategoryModal(true);
  };

  const openEditCategoryModal = (category) => {
    setEditingCategoryId(category.id);
    setCategoryForm({
      name: category?.name || '',
      slug: category?.slug || '',
      order: category?.order ?? 0,
    });
    setShowCategoryModal(true);
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setEditingCategoryId(null);
    setCategoryForm(emptyCategoryForm);
  };

  const handleAddToCart = async (productId, quantity = null) => {
    const qty = quantity || quantityInputs[productId] || 1;
    if (qty < 1) {
      toast.error('Количество должно быть больше 0');
      return;
    }
    
    const product = products.find((p) => p.id === productId);
    if (!product) return;

    setDraftCart((prev) => {
      const prevItem = prev[productId];
      const newQty = (prevItem?.quantity || 0) + qty;
      return {
        ...prev,
        [productId]: { product, quantity: newQty },
      };
    });
    toast.success('Товар добавлен в корзину (черновик)');
  };

  const draftItems = Object.values(draftCart);
  const cartItemsCount = draftItems.reduce(
    (sum, item) => sum + (item.quantity || 0),
    0,
  );

  const handleSubmitDraftOrder = async () => {
    if (!draftItems.length) {
      toast.error('Корзина пуста');
      return;
    }
    // Переносим черновик на сервер перед открытием формы подтверждения
    try {
      setIsLoading(true);
      await shopAPI.clearCart().catch(() => {});
      for (const item of draftItems) {
        await shopAPI.addToCart(item.product.id, item.quantity || 1);
      }
      // Теперь открываем форму подтверждения
      setShowCart(false);
      setShowCheckout(true);
    } catch (error) {
      console.error('Error transferring draft to server:', error);
      toast.error('Ошибка при переносе корзины на сервер');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedBrandGroup(null);
    setSelectedSubcategory(null);
    setShowMobileCategories(false);
  };

  const handleBrandGroupSelect = (brandId) => {
    setSelectedBrandGroup(brandId);
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setShowMobileCategories(false);
  };

  const handleSubcategorySelect = (subcategoryId) => {
    setSelectedSubcategory(subcategoryId);
    setShowMobileCategories(false);
  };

  const handleClearFilters = () => {
    setSelectedCategory(null);
    setSelectedBrandGroup(null);
    setSelectedSubcategory(null);
    setSortBy('default');
  };

  // Фильтруем категории, исключая те, что уже в группах брендов
  const regularCategories = useMemo(() => {
    const brandCategoryIds = new Set();
    Object.values(brandGroups).forEach(group => {
      group.categories.forEach(cat => brandCategoryIds.add(cat.id));
    });
    return categories.filter(cat => !brandCategoryIds.has(cat.id));
  }, [categories, brandGroups]);

  const selectedCategoryIds = useMemo(() => {
    if (selectedCategory) {
      return [selectedCategory];
    }

    if (selectedBrandGroup && brandGroups[selectedBrandGroup]) {
      return brandGroups[selectedBrandGroup].categories.map((cat) => cat.id);
    }

    return null;
  }, [selectedCategory, selectedBrandGroup, brandGroups]);

  const baseFilteredProducts = useMemo(() => {
    if (!selectedCategoryIds || selectedCategoryIds.length === 0) {
      return products;
    }

    const categoryIdSet = new Set(selectedCategoryIds);
    return products.filter((product) => product.category && categoryIdSet.has(product.category.id));
  }, [products, selectedCategoryIds]);

  const availableSubcategories = useMemo(
    () => collectSubcategories(baseFilteredProducts),
    [baseFilteredProducts],
  );

  useEffect(() => {
    if (!selectedSubcategory) return;
    const isStillAvailable = availableSubcategories.some((subcategory) => subcategory.id === selectedSubcategory);
    if (!isStillAvailable) {
      setSelectedSubcategory(null);
    }
  }, [selectedSubcategory, availableSubcategories]);

  const filteredProducts = useMemo(() => {
    let result = [...baseFilteredProducts];

    if (selectedSubcategory) {
      result = result.filter((product) => getSubcategoryByProduct(product.name)?.id === selectedSubcategory);
    }

    if (sortBy === 'name_asc') {
      result.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
    } else if (sortBy === 'name_desc') {
      result.sort((a, b) => b.name.localeCompare(a.name, 'ru'));
    }

    return result;
  }, [baseFilteredProducts, selectedSubcategory, sortBy]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 sm:pt-24 pb-8 sm:pb-12 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#4897d2]"></div>
      </div>
    );
  }

  const showBrandGroups = !selectedCategory && !selectedBrandGroup && !selectedSubcategory;

  return (
    <div className="min-h-screen bg-slate-100 pt-20 sm:pt-24 pb-8 sm:pb-12">
      <div className="w-full max-w-[1360px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Team Shop
              </h1>
              <p className="text-gray-600">Спортивное питание для ваших тренировок</p>
            </div>
            <button
              onClick={() => setShowCart(true)}
              className="relative px-6 py-3 bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] text-white rounded-lg shadow-md hover:shadow-lg hover:opacity-90 transition-all flex items-center gap-2"
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="hidden sm:inline">Корзина</span>
              {cartItemsCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                  {cartItemsCount}
                </span>
              )}
            </button>
            {isAdmin && (
              <button
                onClick={() => setShowAdminModal(true)}
                className="px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Управление магазином
              </button>
            )}
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {/* Mobile Categories Button */}
              <button
              onClick={() => setShowMobileCategories(true)}
              className="lg:hidden px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Menu className="w-4 h-4" />
              Категории
              </button>

            {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  showFilters || selectedCategory || selectedBrandGroup || selectedSubcategory || sortBy !== 'default'
                    ? 'bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-4 h-4" />
                Фильтры
              </button>

            {/* Clear Filters */}
            {(selectedCategory || selectedBrandGroup || selectedSubcategory || sortBy !== 'default') && (
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Сбросить
              </button>
            )}
            </div>

            {/* Advanced Filters */}
            {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 bg-white rounded-lg p-4 shadow-sm">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Категория
                  </label>
                  <select
                    value={
                      selectedBrandGroup
                        ? `brand:${selectedBrandGroup}`
                        : selectedCategory
                        ? `category:${selectedCategory}`
                        : 'all'
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'all') {
                        handleClearFilters();
                        return;
                      }

                      if (value.startsWith('brand:')) {
                        handleBrandGroupSelect(value.replace('brand:', ''));
                        return;
                      }

                      if (value.startsWith('category:')) {
                        handleCategorySelect(Number(value.replace('category:', '')));
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                  >
                    <option value="all">Все товары</option>
                    {Object.values(brandGroups).map((group) => (
                      <option key={group.id} value={`brand:${group.id}`}>
                        {group.title}
                      </option>
                    ))}
                    {regularCategories.map((category) => (
                      <option key={category.id} value={`category:${category.id}`}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Подкатегория
                  </label>
                  <select
                    value={selectedSubcategory || 'all'}
                    onChange={(e) => handleSubcategorySelect(e.target.value === 'all' ? null : e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                  >
                    <option value="all">Все подкатегории</option>
                    {availableSubcategories.map((subcategory) => (
                      <option key={subcategory.id} value={subcategory.id}>
                        {subcategory.label} ({subcategory.count})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Сортировка
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                  >
                    <option value="default">По умолчанию</option>
                    <option value="name_asc">Название: А-Я</option>
                    <option value="name_desc">Название: Я-А</option>
                  </select>
                </div>
              </div>
            )}
          </div>

        {/* Main Content: Sidebar + Products */}
        <div className="flex gap-6">
          {/* Sidebar Categories (Desktop) */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm p-4 sticky top-24">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Категории</h2>
              <button
                onClick={handleClearFilters}
                className={`w-full text-left mb-3 p-3 rounded-lg transition-all ${
                  !selectedCategory && !selectedBrandGroup && !selectedSubcategory
                    ? 'bg-[#4897d2] text-white shadow-md font-medium'
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                }`}
              >
                Все товары
              </button>
              
              {/* Brand Groups */}
              {Object.values(brandGroups).map((group) => (
                <div key={group.id} className="mb-3">
                  <button
                    onClick={() => handleBrandGroupSelect(group.id)}
                    className={`w-full text-left p-3 rounded-lg transition-all ${
                      selectedBrandGroup === group.id
                        ? `bg-gradient-to-r ${group.color} text-white shadow-md`
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="font-semibold mb-1">{group.title}</div>
                    <div className={`text-xs ${selectedBrandGroup === group.id ? 'text-white/90' : 'text-gray-500'}`}>
                      {group.categories.length} категорий
                    </div>
                  </button>
                  {selectedBrandGroup === group.id && (
                    <div className="mt-2 ml-2 space-y-1 border-l border-gray-200 pl-2">
                      <button
                        onClick={() => handleSubcategorySelect(null)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                          !selectedSubcategory
                            ? 'bg-[#4897d2]/10 text-[#2f77b2] font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        Все подкатегории
                      </button>
                      {availableSubcategories.map((subcategory) => (
                        <button
                          key={subcategory.id}
                          onClick={() => handleSubcategorySelect(subcategory.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                            selectedSubcategory === subcategory.id
                              ? 'bg-[#4897d2] text-white font-medium'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {subcategory.label} ({subcategory.count})
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Regular Categories */}
              {regularCategories.length > 0 && (
                <>
                  <div className="border-t border-gray-200 my-4"></div>
                  <div className="space-y-1">
                    <button
                      onClick={() => handleCategorySelect(null)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        !selectedCategory && !selectedBrandGroup
                          ? 'bg-[#4897d2] text-white font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Все товары
                    </button>
                    {regularCategories.map((category) => (
                      <div key={category.id}>
                        <button
                          onClick={() => handleCategorySelect(category.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${
                            selectedCategory === category.id
                              ? 'bg-[#4897d2] text-white font-medium'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <span>{category.name}</span>
                          {selectedCategory === category.id && (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                        {selectedCategory === category.id && (
                          <div className="mt-1 ml-2 space-y-1 border-l border-gray-200 pl-2">
                            <button
                              onClick={() => handleSubcategorySelect(null)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                                !selectedSubcategory
                                  ? 'bg-[#4897d2]/10 text-[#2f77b2] font-medium'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              Все подкатегории
                            </button>
                            {availableSubcategories.map((subcategory) => (
                              <button
                                key={subcategory.id}
                                onClick={() => handleSubcategorySelect(subcategory.id)}
                                className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                                  selectedSubcategory === subcategory.id
                                    ? 'bg-[#4897d2] text-white font-medium'
                                    : 'text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                {subcategory.label} ({subcategory.count})
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
                          )}
                        </div>
          </aside>

          {/* Products Grid */}
          <main className="flex-1 min-w-0">
            {/* Brand Groups View (when no category selected) */}
            {showBrandGroups && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Выберите бренд</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.values(brandGroups).map((group) => (
                    <button
                      key={group.id}
                      onClick={() => handleBrandGroupSelect(group.id)}
                      className={`bg-gradient-to-r ${group.color} text-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 text-left`}
                    >
                      <h3 className="text-3xl font-bold mb-3">{group.title}</h3>
                      <p className="text-white/90 mb-4">{group.description}</p>
                      <div className="flex items-center gap-2 text-white/80">
                        <span className="text-sm">
                          {group.categories.length} {group.categories.length === 1 ? 'категория' : 'категорий'}
                        </span>
                        <ChevronRight className="w-5 h-5" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Products Grid */}
            {!showBrandGroups && (
              <>
                {/* Results Header */}
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {selectedBrandGroup
                      ? `${brandGroups[selectedBrandGroup]?.title || 'Товары'} (${filteredProducts.length})`
                      : selectedCategory
                      ? `${categories.find(c => c.id === selectedCategory)?.name || 'Товары'} (${filteredProducts.length})`
                      : selectedSubcategory
                      ? `${SUBCATEGORY_RULES.find((item) => item.id === selectedSubcategory)?.label || 'Товары'} (${filteredProducts.length})`
                      : `Все товары (${filteredProducts.length})`}
                  </h2>
                </div>

                {filteredProducts.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                    {filteredProducts.map((product) => (
                      <div key={product.id} className="relative">
                        <ProductCard
                          product={product}
                          onAddToCart={handleAddToCart}
                        />
                        {isAdmin && (
                          <div className="absolute top-2 right-2 z-10 inline-flex gap-2">
                            <button
                              type="button"
                              onClick={() => openEditProductModal(product)}
                              className="p-2 rounded-full bg-white/90 hover:bg-blue-50 text-[#4897d2] shadow"
                              title="Редактировать товар"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => openConfirm({
                                title: 'Подтвердите удаление',
                                description: 'Удалить товар?',
                                actionType: 'delete_product',
                                payload: { productId: product.id },
                              })}
                              className="p-2 rounded-full bg-white/90 hover:bg-red-50 text-red-600 shadow"
                              title="Удалить товар"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-white rounded-lg shadow-sm">
                    <p className="text-gray-600 text-lg">Товары не найдены</p>
                    <button
                      onClick={handleClearFilters}
                      className="mt-4 text-[#4897d2] hover:underline"
                    >
                      Сбросить фильтры
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* Mobile Categories Sidebar */}
      {showMobileCategories && (
        <div
          className="fixed inset-0 bg-black/50 z-50 lg:hidden"
          onClick={() => setShowMobileCategories(false)}
        >
          <div
            className="bg-white h-full w-80 max-w-[85vw] shadow-xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-gray-900">Категории</h2>
              <button
                onClick={() => setShowMobileCategories(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              <button
                onClick={handleClearFilters}
                className={`w-full text-left p-4 rounded-lg transition-all ${
                  !selectedCategory && !selectedBrandGroup && !selectedSubcategory
                    ? 'bg-[#4897d2] text-white shadow-md font-medium'
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                }`}
              >
                Все товары
              </button>
              {/* Brand Groups */}
              {Object.values(brandGroups).map((group) => (
                <div key={group.id}>
                  <button
                    onClick={() => handleBrandGroupSelect(group.id)}
                    className={`w-full text-left p-4 rounded-lg transition-all ${
                      selectedBrandGroup === group.id
                        ? `bg-gradient-to-r ${group.color} text-white shadow-md`
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="font-semibold mb-1">{group.title}</div>
                    <div className={`text-xs ${selectedBrandGroup === group.id ? 'text-white/90' : 'text-gray-500'}`}>
                      {group.categories.length} категорий
                    </div>
                  </button>
                  {selectedBrandGroup === group.id && (
                    <div className="mt-2 ml-2 space-y-1 border-l border-gray-200 pl-2">
                      <button
                        onClick={() => handleSubcategorySelect(null)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                          !selectedSubcategory
                            ? 'bg-[#4897d2]/10 text-[#2f77b2] font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        Все подкатегории
                      </button>
                      {availableSubcategories.map((subcategory) => (
                        <button
                          key={subcategory.id}
                          onClick={() => handleSubcategorySelect(subcategory.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                            selectedSubcategory === subcategory.id
                              ? 'bg-[#4897d2] text-white font-medium'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {subcategory.label} ({subcategory.count})
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Regular Categories */}
              {regularCategories.length > 0 && (
                <>
                  <div className="border-t border-gray-200 my-4"></div>
                  <button
                    onClick={() => handleCategorySelect(null)}
                    className={`w-full text-left px-4 py-3 rounded-lg text-sm transition-colors ${
                      !selectedCategory && !selectedBrandGroup
                        ? 'bg-[#4897d2] text-white font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Все товары
                  </button>
                  {regularCategories.map((category) => (
                    <div key={category.id}>
                      <button
                        onClick={() => handleCategorySelect(category.id)}
                        className={`w-full text-left px-4 py-3 rounded-lg text-sm transition-colors ${
                          selectedCategory === category.id
                            ? 'bg-[#4897d2] text-white font-medium'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {category.name}
                      </button>
                      {selectedCategory === category.id && (
                        <div className="mt-2 ml-2 space-y-1 border-l border-gray-200 pl-2">
                          <button
                            onClick={() => handleSubcategorySelect(null)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                              !selectedSubcategory
                                ? 'bg-[#4897d2]/10 text-[#2f77b2] font-medium'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            Все подкатегории
                          </button>
                          {availableSubcategories.map((subcategory) => (
                            <button
                              key={subcategory.id}
                              onClick={() => handleSubcategorySelect(subcategory.id)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                                selectedSubcategory === subcategory.id
                                  ? 'bg-[#4897d2] text-white font-medium'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              {subcategory.label} ({subcategory.count})
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showAdminModal && isAdmin && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowAdminModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 p-5 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Управление магазином</h2>
              <button onClick={() => setShowAdminModal(false)} className="p-2 rounded-lg hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setAdminTab('product')}
                  className={`px-4 py-2 rounded-lg text-sm ${adminTab === 'product' ? 'bg-[#4897d2] text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  Товары
                </button>
                <button
                  onClick={() => setAdminTab('category')}
                  className={`px-4 py-2 rounded-lg text-sm ${adminTab === 'category' ? 'bg-[#4897d2] text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  Категории
                </button>
              </div>

              {adminTab === 'product' && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900">Товары</h3>
                  <p className="text-sm text-gray-600">Добавление товара выполняется через модальное окно.</p>
                  <button
                    type="button"
                    onClick={openCreateProductModal}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4897d2] text-white hover:opacity-90"
                  >
                    <Plus className="w-4 h-4" />
                    Добавить товар
                  </button>
                </div>
              )}

              {adminTab === 'category' && (
                <div className="space-y-6">
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-gray-900">Категории</h3>
                    <p className="text-sm text-gray-600">Добавление категории выполняется через модальное окно.</p>
                    <button
                      type="button"
                      onClick={openCreateCategoryModal}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4897d2] text-white hover:opacity-90"
                    >
                      <Plus className="w-4 h-4" />
                      Добавить категорию
                    </button>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Удалить категорию</h3>
                    <div className="space-y-2">
                      {categories.map((category) => (
                        <div key={category.id} className="flex items-center justify-between border rounded-lg px-3 py-2">
                          <span>{category.name}</span>
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openEditCategoryModal(category)}
                              className="inline-flex items-center gap-1 text-[#4897d2] hover:text-[#2f77b2]"
                            >
                              <Pencil className="w-4 h-4" />
                              Изменить
                            </button>
                            <button
                              type="button"
                              onClick={() => openConfirm({
                                title: 'Подтвердите удаление',
                                description: 'Удалить категорию? Товары останутся без категории.',
                                actionType: 'delete_category',
                                payload: { categoryId: category.id },
                              })}
                              className="inline-flex items-center gap-1 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                              Удалить
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showProductModal && isAdmin && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={closeProductModal}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 p-5 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">{editingProductId ? 'Редактировать товар' : 'Добавить товар'}</h3>
              <button onClick={closeProductModal} className="p-2 rounded-lg hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <select
                  value={productForm.category_id}
                  onChange={(e) => setProductForm((prev) => ({ ...prev, category_id: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Без категории</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <input
                  className="border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Название *"
                  value={productForm.name}
                  onChange={(e) => setProductForm((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <textarea
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows={3}
                placeholder="Описание"
                value={productForm.description}
                onChange={(e) => setProductForm((prev) => ({ ...prev, description: e.target.value }))}
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  className="border border-gray-300 rounded-lg px-3 py-2"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Цена *"
                  value={productForm.price}
                  onChange={(e) => setProductForm((prev) => ({ ...prev, price: e.target.value }))}
                />
                <input
                  className="border border-gray-300 rounded-lg px-3 py-2"
                  type="number"
                  min="0"
                  placeholder="Упаковка (шт.)"
                  value={productForm.pack_quantity}
                  onChange={(e) => setProductForm((prev) => ({ ...prev, pack_quantity: e.target.value }))}
                />
                <input
                  className="border border-gray-300 rounded-lg px-3 py-2"
                  type="number"
                  min="0"
                  placeholder="Порядок"
                  value={productForm.order}
                  onChange={(e) => setProductForm((prev) => ({ ...prev, order: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  className="border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Ссылка на картинку"
                  value={productForm.external_image_url}
                  onChange={(e) => setProductForm((prev) => ({ ...prev, external_image_url: e.target.value }))}
                />
                <input
                  className="border border-gray-300 rounded-lg px-3 py-2"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setProductForm((prev) => ({ ...prev, image: e.target.files?.[0] || null }))}
                />
              </div>
              <p className="text-xs text-gray-500">Приоритет отображения: загруженный файл, затем ссылка.</p>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={productForm.is_available}
                  onChange={(e) => setProductForm((prev) => ({ ...prev, is_available: e.target.checked }))}
                />
                Товар доступен
              </label>
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-5 flex gap-2">
              <button
                type="button"
                disabled={adminLoading}
                onClick={() => openConfirm({
                  title: editingProductId ? 'Подтвердите изменение' : 'Подтвердите добавление',
                  description: editingProductId ? 'Сохранить изменения товара?' : 'Добавить новый товар?',
                  actionType: editingProductId ? 'update_product' : 'create_product',
                  payload: null,
                })}
                className="px-4 py-2 rounded-lg text-white bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] disabled:opacity-50"
              >
                {adminLoading ? 'Сохраняем...' : editingProductId ? 'Сохранить товар' : 'Добавить товар'}
              </button>
              <button
                type="button"
                onClick={closeProductModal}
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {showCategoryModal && isAdmin && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={closeCategoryModal}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 p-5 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">{editingCategoryId ? 'Редактировать категорию' : 'Добавить категорию'}</h3>
              <button onClick={closeCategoryModal} className="p-2 rounded-lg hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  className="border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Название *"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm((prev) => ({ ...prev, name: e.target.value }))}
                />
                <input
                  className="border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="slug *"
                  value={categoryForm.slug}
                  onChange={(e) => setCategoryForm((prev) => ({ ...prev, slug: e.target.value }))}
                />
                <input
                  className="border border-gray-300 rounded-lg px-3 py-2"
                  type="number"
                  min="0"
                  placeholder="Порядок"
                  value={categoryForm.order}
                  onChange={(e) => setCategoryForm((prev) => ({ ...prev, order: e.target.value }))}
                />
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-5 flex gap-2">
              <button
                type="button"
                disabled={adminLoading}
                onClick={() => openConfirm({
                  title: editingCategoryId ? 'Подтвердите изменение' : 'Подтвердите добавление',
                  description: editingCategoryId ? 'Сохранить изменения категории?' : 'Добавить новую категорию?',
                  actionType: editingCategoryId ? 'update_category' : 'create_category',
                  payload: null,
                })}
                className="px-4 py-2 rounded-lg text-white bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] disabled:opacity-50"
              >
                {adminLoading ? 'Сохраняем...' : editingCategoryId ? 'Сохранить категорию' : 'Добавить категорию'}
              </button>
              <button
                type="button"
                onClick={closeCategoryModal}
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" onClick={closeConfirm}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-lg font-bold text-gray-900">{confirmModal.title}</h4>
            <p className="text-sm text-gray-600 mt-2">{confirmModal.description}</p>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={confirmAction}
                disabled={adminLoading}
                className="px-4 py-2 rounded-lg bg-[#4897d2] text-white hover:opacity-90 disabled:opacity-60"
              >
                Подтвердить
              </button>
              <button
                type="button"
                onClick={closeConfirm}
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cart Modal (локальный черновик) */}
      {showCart && !showCheckout && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCart(false);
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <ShoppingCart className="w-6 h-6 text-[#4897d2]" />
                Корзина (черновик)
              </h2>
              <button
                onClick={() => setShowCart(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {draftItems.length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                  Корзина пуста
                </div>
              ) : (
                <div className="space-y-4">
                  {draftItems.map((item) => (
                    <div
                      key={item.product.id}
                      className="flex items-center justify-between border border-gray-200 rounded-xl p-4"
                    >
                      <div>
                        <div className="font-semibold text-gray-900">
                          {item.product.name}
                        </div>
                        {item.product.pack_quantity && (
                          <div className="text-xs text-gray-500">
                            {item.product.pack_quantity} шт. в упаковке
                          </div>
                        )}
                        <div className="text-sm text-gray-600 mt-1">
                          {item.product.price}₪ × {item.quantity}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                          <button
                          className="px-3 py-1 border rounded-full text-gray-600 hover:bg-gray-100"
                          onClick={() =>
                            setDraftCart((prev) => {
                              const current = prev[item.product.id];
                              const nextQty = (current?.quantity || 1) - 1;
                              if (nextQty <= 0) {
                                const copy = { ...prev };
                                delete copy[item.product.id];
                                return copy;
                              }
                              return {
                              ...prev,
                                [item.product.id]: {
                                  ...current,
                                  quantity: nextQty,
                                },
                              };
                            })
                          }
                          >
                            −
                          </button>
                        <span className="min-w-[2rem] text-center font-semibold">
                          {item.quantity}
                        </span>
                        <button
                          className="px-3 py-1 border rounded-full text-gray-600 hover:bg-gray-100"
                          onClick={() =>
                            setDraftCart((prev) => {
                              const current = prev[item.product.id];
                              const nextQty = (current?.quantity || 0) + 1;
                              return {
                                ...prev,
                                [item.product.id]: {
                                  ...current,
                                  quantity: nextQty,
                                },
                              };
                            })
                          }
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-white rounded-b-2xl flex gap-3">
              <div className="flex-1">
                <div className="text-sm text-gray-600 mb-1">Итого:</div>
                <div className="text-2xl font-bold text-[#4897d2]">
                  {draftItems.reduce((sum, item) => {
                    return sum + (parseFloat(item.product.price) * (item.quantity || 1));
                  }, 0).toFixed(2)}₪
                </div>
              </div>
              <button
                onClick={handleSubmitDraftOrder}
                disabled={draftItems.length === 0 || isLoading}
                className="flex-1 bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                Оформить заказ
              </button>
              <button
                onClick={() => setShowCart(false)}
                className="px-4 border border-gray-300 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-50 transition-colors"
              >
                Закрыть
              </button>
            </div>
          </div>
          </div>
        )}

      {/* Checkout Modal (форма подтверждения) */}
      {showCheckout && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCheckout(false);
              setShowCart(true);
            }
          }}
        >
          <Checkout
            cart={null}
            onBack={() => {
              setShowCheckout(false);
              setShowCart(true);
            }}
            onSuccess={async () => {
              // Checkout уже создал заказ из серверной корзины, просто очищаем локальную корзину
              setDraftCart({});
              setShowCart(false);
              setShowCheckout(false);
            }}
          />
        </div>
      )}

      {/* Product Description Modal */}
      {selectedProduct && (
        <ProductDescriptionModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={handleAddToCart}
        />
      )}
    </div>
  );
};

export default ShopPage;
